import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { ArrowLeft, Plus, Pencil, ExternalLink } from "lucide-react";
import { AppLayout } from "@/components/layout/AppLayout";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { ProjectForm } from "@/components/projects/ProjectForm";
import type { ProjectFormData } from "@/components/projects/ProjectForm";
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetDescription,
} from "@/components/ui/sheet";
import {
  useProjectTree,
  useCreateProject,
  useUpdateProject,
} from "@/hooks/useProjects";
import { useCommandStyle } from "@/contexts/CommandStyleContext";
import type { ProjectTreeNode } from "@/hooks/useProjects";
import { toast } from "sonner";
import type { Project } from "@/lib/types";
import { formatError } from "@/lib/utils";

function ProjectNode({
  node,
  depth,
  statusColor,
  navigate,
  onEdit,
}: {
  node: ProjectTreeNode;
  depth: number;
  statusColor: Record<Project["status"], string>;
  navigate: (path: string) => void;
  onEdit: (p: Project) => void;
}) {
  return (
    <>
      <div
        className="flex items-center gap-3 p-3 rounded-lg border border-border bg-card hover:bg-accent/10 transition-colors cursor-pointer"
        style={{ marginLeft: depth * 24 }}
        onClick={() => navigate(`/projects/${node.slug}`)}
      >
        <div
          className="w-4 h-4 rounded-full shrink-0"
          style={{ backgroundColor: node.color }}
        />
        <div className="flex-1 min-w-0">
          <p className="text-sm font-medium truncate">{node.name}</p>
          <p className="text-xs text-muted-foreground truncate">
            {node.description || node.slug}
          </p>
        </div>
        {node.url && (
          <a
            href={node.url.startsWith("http") ? node.url : `https://${node.url}`}
            target="_blank"
            rel="noopener noreferrer"
            onClick={(e) => e.stopPropagation()}
            className="text-muted-foreground hover:text-foreground shrink-0"
          >
            <ExternalLink className="h-3.5 w-3.5" />
          </a>
        )}
        <Badge variant="secondary" className={statusColor[node.status]}>
          {node.status}
        </Badge>
        <Button
          variant="ghost"
          size="icon"
          className="shrink-0"
          onClick={(e) => {
            e.stopPropagation();
            onEdit(node);
          }}
        >
          <Pencil className="h-4 w-4" />
        </Button>
      </div>
      {node.children.map((child) => (
        <ProjectNode
          key={child.id}
          node={child}
          depth={depth + 1}
          statusColor={statusColor}
          navigate={navigate}
          onEdit={onEdit}
        />
      ))}
    </>
  );
}

function ProjectEditSheet({
  project,
  open,
  onOpenChange,
}: {
  project: Project | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}) {
  const updateProject = useUpdateProject();

  if (!project) return null;

  const handleUpdate = (data: ProjectFormData) => {
    updateProject.mutate(
      {
        id: project.id,
        name: data.name,
        slug: data.slug,
        color: data.color,
        status: data.status,
        description: data.description,
        deadline: data.deadline,
        url: data.url,
        parent_id: data.parent_id,
      },
      {
        onSuccess: () => {
          toast("Project updated");
          onOpenChange(false);
        },
        onError: (err) => toast.error(formatError(err)),
      }
    );
  };

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent className="sm:max-w-md overflow-y-auto">
        <SheetHeader>
          <SheetTitle>Edit Project</SheetTitle>
          <SheetDescription>
            Update the details for {project.name}.
          </SheetDescription>
        </SheetHeader>

        <div className="mt-6">
          <ProjectForm
            project={project}
            onSubmit={handleUpdate}
            onCancel={() => onOpenChange(false)}
            isPending={updateProject.isPending}
          />
        </div>
      </SheetContent>
    </Sheet>
  );
}

export default function Projects() {
  const navigate = useNavigate();
  const { copy } = useCommandStyle();
  const { data: tree, projects, isLoading } = useProjectTree();
  const createProject = useCreateProject();

  const [showForm, setShowForm] = useState(false);
  const [editProject, setEditProject] = useState<Project | null>(null);

  const handleCreate = (data: ProjectFormData) => {
    createProject.mutate(
      {
        name: data.name,
        slug: data.slug,
        color: data.color,
        description: data.description,
        deadline: data.deadline,
        url: data.url,
        parent_id: data.parent_id,
      },
      {
        onSuccess: () => {
          toast("Project created");
          setShowForm(false);
        },
        onError: (err) => toast.error(formatError(err)),
      }
    );
  };

  const statusColor: Record<Project["status"], string> = {
    active: "bg-green-500/20 text-green-400",
    paused: "bg-yellow-500/20 text-yellow-400",
    archived: "bg-muted text-muted-foreground",
  };

  return (
    <AppLayout>
      <div className="flex items-center gap-3 mb-6">
        <Button variant="ghost" size="icon" aria-label="Back to dashboard" onClick={() => navigate("/")}>
          <ArrowLeft className="h-5 w-5" />
        </Button>
        <h1 className="text-xl font-semibold flex-1">{copy.projects}</h1>
        {!showForm && (
          <Button size="sm" onClick={() => setShowForm(true)}>
            <Plus className="h-4 w-4 mr-1" />
            {copy.addButton}
          </Button>
        )}
      </div>

      {/* Create form */}
      {showForm && (
        <div className="mb-6 p-4 rounded-lg border border-border bg-card">
          <h2 className="text-sm font-medium mb-4">New Project</h2>
          <ProjectForm
            onSubmit={handleCreate}
            onCancel={() => setShowForm(false)}
            isPending={createProject.isPending}
          />
        </div>
      )}

      {/* Project list */}
      {isLoading ? (
        <p className="text-muted-foreground text-sm">Loading...</p>
      ) : !projects || projects.length === 0 ? (
        <div className="text-center py-8">
          <p className="text-muted-foreground mb-2">{copy.emptyProjects}</p>
        </div>
      ) : (
        <div className="space-y-2">
          {tree.map((node) => (
            <ProjectNode
              key={node.id}
              node={node}
              depth={0}
              statusColor={statusColor}
              navigate={navigate}
              onEdit={(p) => {
                setEditProject(p);
              }}
            />
          ))}
        </div>
      )}
      <ProjectEditSheet
        project={editProject}
        open={!!editProject}
        onOpenChange={(o) => !o && setEditProject(null)}
      />
    </AppLayout>
  );
}
